// src/features/people/components/AssignWorkModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaSearch, FaPlus } from 'react-icons/fa';
import WorksService from '../../works/WorksService';

/**
 * Modal para asignar una obra a una persona
 * 
 * @param {boolean} show - Controla la visibilidad del modal
 * @param {Function} onHide - Función para cerrar el modal
 * @param {string} personId - ID de la persona a la que se asignará la obra
 * @param {Array} currentWorks - Lista de obras ya asignadas a la persona
 * @param {Function} onWorkAssigned - Callback cuando se asigna la obra con éxito
 */
const AssignWorkModal = ({ show, onHide, personId, currentWorks = [], onWorkAssigned }) => {
  // Estados
  const [loading, setLoading] = useState(true);
  const [works, setWorks] = useState([]);
  const [filteredWorks, setFilteredWorks] = useState([]);
  const [selectedWork, setSelectedWork] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Cargar obras disponibles al abrir el modal
  useEffect(() => {
    if (show) {
      loadWorks();
    }
  }, [show]);

  // Filtrar obras cuando cambia el término de búsqueda o la lista de obras
  useEffect(() => {
    filterWorks();
  }, [searchTerm, works]);

  // Función para cargar las obras desde la API
  const loadWorks = async () => {
    try {
      setLoading(true);
      const worksData = await WorksService.getAllWorks();
      
      // Filtrar para excluir las obras ya asignadas
      const currentWorkIds = currentWorks.map(work => work.id);
      const availableWorks = worksData.filter(work => !currentWorkIds.includes(work.id));
      
      setWorks(availableWorks);
      setFilteredWorks(availableWorks);
    } catch (error) {
      console.error('Error al cargar obras:', error);
      setError('Error al cargar la lista de obras disponibles');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar obras según el término de búsqueda
  const filterWorks = () => {
    if (!searchTerm.trim()) {
      setFilteredWorks(works);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = works.filter(work => {
      return (
        work.name.toLowerCase().includes(term) ||
        (work.location && work.location.toLowerCase().includes(term))
      );
    });
    
    setFilteredWorks(filtered);
  };

  // Seleccionar una obra
  const handleSelectWork = (work) => {
    setSelectedWork(work);
    setError('');
  };

  // Asignar la obra seleccionada a la persona
  const handleAssignWork = async () => {
    if (!selectedWork) {
      setError('Por favor seleccione una obra');
      return;
    }
    
    try {
      setAssigning(true);
      setError('');
      
      // Obtener todas las personas asignadas actualmente a la obra
      const workDetails = await WorksService.getWork(selectedWork.id);
      const currentPersons = workDetails.persons || [];
      
      // Crear la lista de IDs de personas que incluye las existentes más la nueva
      const personIds = [...currentPersons.map(person => person.id), personId];
      
      // Llamar al API para asignar
      const result = await WorksService.assignPersonsToWork(personIds, selectedWork.id);
      
      if (result.statusCode >= 400) {
        throw new Error(result.data.message || 'Error al asignar la obra');
      }
      
      // Llamar al callback de éxito
      if (onWorkAssigned) {
        onWorkAssigned();
      }
      
      // Cerrar el modal
      onHide();
    } catch (error) {
      console.error('Error al asignar obra:', error);
      setError(`Error al asignar la obra: ${error.message || 'Error desconocido'}`);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className='text-center w-100'>Asignar Obra</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        
        <p className="text-muted mb-3">
          Seleccione la obra que desea asignar a esta persona.
        </p>
        
        {/* Búsqueda */}
        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Buscar por nombre o ubicación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
          />
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
        </InputGroup>
        
        {/* Lista de obras */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
            <p className="mt-2">Cargando obras disponibles...</p>
          </div>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
            {filteredWorks.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No hay obras disponibles{searchTerm ? ' que coincidan con la búsqueda' : ''}
              </div>
            ) : (
              <ul className="list-group">
                {filteredWorks.map(work => (
                  <li
                    key={work.id}
                    className={`list-group-item ${selectedWork && selectedWork.id === work.id ? 'active' : ''}`}
                    onClick={() => handleSelectWork(work)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold">{work.name}</div>
                        {work.location && <div className="small">{work.location}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={assigning}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAssignWork}
          disabled={!selectedWork || assigning}
        >
          {assigning ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Asignando...
            </>
          ) : (
            <>
              <FaPlus className="me-1" /> Asignar Obra
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignWorkModal;